import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { client } from '@/api/client';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import UserManagementTab from '../components/admin/UserManagementTab';
import BillingTab from '../components/admin/BillingTab';
import { Button } from '@/components/ui/button';
import { Plus, Fuel, FileCheck, Truck, DollarSign, BarChart3, Check, X, MessageSquare, Pencil, Download, Upload, Calendar, Map as MapIcon, ExternalLink, Settings, LayoutGrid, List, Users, ClipboardCheck, FileText, Clock, PackageCheck, UserPlus, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import FleetMapPanel from '../components/admin/FleetMapPanel';
import AdminLoadChat from '../components/AdminLoadChat';
import NewLoadModal from '../components/admin/NewLoadModal';
import LoadsCsvImportModal from '../components/admin/LoadsCsvImportModal';
import EditLoadModal from '../components/admin/EditLoadModal';
import LoadDetailModal from '../components/admin/LoadDetailModal';
import SettlementReviewModal from '../components/admin/SettlementReviewModal';
import PayrollTab from '../components/admin/PayrollTab';
import DriverManagement from '../components/admin/DriverManagement';
import PendingApprovalsTab from '../components/admin/PendingApprovalsTab';
import CompanySettingsTab from '../components/admin/CompanySettingsTab';
import PayDeductionSettingsTab from '../components/admin/PayDeductionSettingsTab';
import TaxFormsTab from '../components/admin/TaxFormsTab';
import FuelAdvanceApprovalModal from '../components/admin/FuelAdvanceApprovalModal';
import RegeneratePdfButton from '../components/admin/RegeneratePdfButton';
import PayrollHistoryTab from '../components/admin/PayrollHistoryTab';
import ExpiryAlertBanner from '../components/admin/ExpiryAlertBanner';
import BrokerInvoicesTab from '../components/admin/BrokerInvoicesTab';
import AddressBookTab from '../components/admin/AddressBookTab';
import AssetsTab from '../components/admin/AssetsTab';
import ServiceRecordsTab from '../components/admin/ServiceRecordsTab';
import PLReport from '../components/admin/reports/PLReport';
import DriverSettlementsReport from '../components/admin/reports/DriverSettlementsReport';
import DriverPayReport from '../components/admin/reports/DriverPayReport';
import ExpensesReport from '../components/admin/reports/ExpensesReport';
import IFTAReport from '../components/admin/reports/IFTAReport';
import IFTAAuditReport from '../components/admin/reports/IFTAAuditReport';
import CustomerReport from '../components/admin/reports/CustomerReport';
import UnitRevenueReport from '../components/admin/reports/UnitRevenueReport';
import MilesReport from '../components/admin/reports/MilesReport';
import MaintenanceReport from '../components/admin/reports/MaintenanceReport';
import TaxReport from '../components/admin/reports/TaxReport';
import UserSettlementsReport from '../components/admin/reports/UserSettlementsReport';
import CarrierSettlementsReport from '../components/admin/reports/CarrierSettlementsReport';
import CarrierPayReport from '../components/admin/reports/CarrierPayReport';
import DispatcherSettlementsReport from '../components/admin/reports/DispatcherSettlementsReport';
import DispatcherManagementReport from '../components/admin/reports/DispatcherManagementReport';
import IRPStateMilesReport from '../components/admin/reports/IRPStateMilesReport';
import FuelExpensesReport from '../components/admin/reports/FuelExpensesReport';
import ReeferFuelExpensesReport from '../components/admin/reports/ReeferFuelExpensesReport';
import FuelVendorReport from '../components/admin/reports/FuelVendorReport';
import UnitOperatingIncomeReport from '../components/admin/reports/UnitOperatingIncomeReport';
import MPGReport from '../components/admin/reports/MPGReport';
import UnitMilesReport from '../components/admin/reports/UnitMilesReport';
import OTRDaysReport from '../components/admin/reports/OTRDaysReport';
import LaneReport from '../components/admin/reports/LaneReport';
import TripsTab from '../components/admin/TripsTab';
import HomeDashboard from '../components/admin/HomeDashboard';
import { cn } from '@/lib/utils';
import { numOrDefault } from '@/lib/invoiceHelpers';

function InvoiceDropdown({ hasInvoice, onNavigate, onRelease }) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const btnRef = useRef(null);
  // Creating an Invoice and marking a load as a Claim are accounting
  // duties, not dispatch's -- both were removed from here. Dispatch's
  // only job on a Delivered load is to Release it to Accounting; once an
  // Invoice draft already exists (auto-created on delivery, or added by
  // accounting themselves), dispatch can still peek at it via View Draft
  // Invoice, but everything else happens from Accounting > Broker
  // Invoices now.
  //
  // No invoice yet -- nothing to pick from a menu, so the button itself
  // just releases directly on click instead of opening a dropdown.
  if (!hasInvoice) {
    return (
      <button
        onClick={onRelease}
        className="text-[10px] font-bold px-2 py-1 rounded border transition-colors whitespace-nowrap bg-amber-500/20 border-amber-500/40 text-amber-400 hover:bg-amber-500/30"
      >
        Release to Accounting
      </button>
    );
  }
  // The table's own overflow-x-auto/overflow-hidden wrappers clip an
  // absolutely-positioned menu (it was getting cut off with no way to
  // see the menu -- reported against a live screenshot). Render the menu
  // in a portal at the body level, positioned from the button's own
  // viewport rect, so it's never clipped by an ancestor.
  const toggleOpen = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
    }
    setOpen(o => !o);
  };
  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={toggleOpen}
        className="text-[10px] font-bold px-2 py-1 rounded border transition-colors whitespace-nowrap bg-teal-500/20 border-teal-500/40 text-teal-400 hover:bg-teal-500/30"
        title="Load actions"
      >
        Draft Invoice ▾
      </button>
      {open && menuPos && createPortal(
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            style={{ position: 'fixed', top: menuPos.top, right: menuPos.right }}
            className="z-50 bg-card border border-border rounded-xl shadow-xl overflow-hidden min-w-[180px]"
          >
            <button onClick={() => { setOpen(false); onRelease(); }} className="w-full text-left px-4 py-2.5 text-xs text-green-400 hover:bg-green-500/10 transition-colors border-b border-border">
              ✅ Release to Accounting
            </button>
            <button onClick={() => { setOpen(false); onNavigate(); }} className="w-full text-left px-4 py-2.5 text-xs text-blue-400 hover:bg-blue-500/10 transition-colors">
              👁 View Draft Invoice
            </button>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

const ALL_PARENT_TABS = [
  // Top-level, above Dispatch -- the post-login landing tab (see Layout.jsx's
  // / redirect and ROLE_TAB_ACCESS below, which puts it first).
  { id: 'dashboard', label: 'Dashboard', subs: [] },
  { id: 'dispatch', label: 'Dispatch', subs: ['loads', 'trips', 'map', 'address-book'] },
  // 'user-management' (Staff/Employees + Invitations) lives here, not
  // Settings -- HR only ever has access to the 'hr' parent tab
  // (ROLE_TAB_ACCESS below), so it had no way to reach staff
  // management or send invites while it sat under Settings.
  { id: 'hr', label: 'HR', subs: ['drivers', 'approvals', 'user-management'] },
  // Payroll folded into Accounting -- same department, one place to look
  // for anything money-related.
  { id: 'accounting', label: 'Accounting', subs: ['settlements', 'payroll', 'advances', 'broker-invoices', 'factoring', 'pay-settings', 'tax-forms'] },
  { id: 'reports', label: 'Reports', subs: [
    'report-tax', 'report-pl',
    'report-driver-settlements', 'report-driver-pay', 'report-user-settlements',
    'report-carrier-settlements', 'report-carrier-pay',
    'report-dispatcher-settlements', 'report-dispatcher-management',
    'report-expenses', 'report-irp-miles', 'report-fuel-expenses', 'report-reefer-fuel', 'report-fuel-vendor',
    'report-customer', 'report-ifta', 'report-ifta-audit', 'report-maintenance',
    'report-unit-revenue', 'report-unit-operating-income', 'report-mpg', 'report-unit-miles', 'report-miles', 'report-otr-days',
    'report-lane',
  ] },
  { id: 'maintenance', label: 'Maintenance', subs: ['assets', 'service-records'] },
  { id: 'settings', label: 'Settings', subs: ['company', 'billing'], icon: Settings },
];

const ROLE_TAB_ACCESS = {
  super_admin: ['dashboard', 'dispatch', 'hr', 'accounting', 'reports', 'maintenance', 'settings'],
  hr: ['dashboard', 'hr'],
  accounting: ['dashboard', 'accounting'],
  dispatch: ['dashboard', 'dispatch'],
};

// Plan-based restrictions on top of role access, mirrored from Layout.jsx's
// PLAN_BLOCKED_SECTIONS (kept as a parallel constant here since AdminPanel
// computes its own PARENT_TABS/subs independently of the sidebar). An
// Owner-Operator is a single-person operation running one truck: Dispatch
// (multi-driver board), HR, and Maintenance don't apply, and the ~25-report
// suite built for fleet dispatch/accounting is overkill -- persona research
// this session found O/Os only reach for P&L and IRP Miles day-to-day.
const OWNER_OP_BLOCKED_PARENTS = ['dispatch', 'hr', 'maintenance'];
// Lane profitability answers "which lanes actually make money" -- valuable
// enough to a solo owner-operator deciding what freight to keep running
// that it stays in the minimal set alongside P&L and IRP Miles.
const OWNER_OP_REPORT_SUBS = ['report-pl', 'report-irp-miles', 'report-lane'];

const SUB_LABELS_ALL = {
  loads: 'Loads',
  trips: 'Trips',
  map: 'Fleet Map',
  drivers: 'Workforce',
  approvals: 'Approvals',
  payroll: 'Payroll',
  settlements: 'Driver Pay Mgt',
  advances: 'Fuel Advances',
  'broker-invoices': 'Invoices',
  factoring: 'Factoring',
  'pay-settings': 'Pay & Deductions',
  'tax-forms': 'W-2 & W-4 Compliance',
  'report-tax': 'Tax',
  'report-pl': 'Profit & Loss',
  'report-driver-settlements': 'Driver Settlements',
  'report-driver-pay': 'Driver Pay Report',
  'report-user-settlements': 'User Settlements',
  'report-carrier-settlements': 'Carrier Settlements',
  'report-carrier-pay': 'Carrier Pay',
  'report-dispatcher-settlements': 'Dispatcher Settlements',
  'report-dispatcher-management': 'Dispatcher Management',
  'report-expenses': 'Expenses',
  'report-irp-miles': 'IRP - State Miles',
  'report-fuel-expenses': 'Fuel Expenses',
  'report-reefer-fuel': 'Reefer Fuel Expenses',
  'report-fuel-vendor': 'Fuel Vendor',
  'report-ifta': 'Quarterly IFTA',
  'report-ifta-audit': 'IFTA Audit',
  'report-customer': 'Customer Report',
  'report-unit-revenue': 'Unit Revenue',
  'report-unit-operating-income': 'Unit Operating Income',
  'report-mpg': 'Miles per Gallon',
  'report-unit-miles': 'Unit Miles',
  'report-miles': 'Miles Report (Driver)',
  'report-maintenance': 'Quarterly Maintenance',
  'report-otr-days': 'Over the Road Days',
  'report-lane': 'Lane Report',
  company: 'Company Settings',
  billing: 'Billing',
  'address-book': 'Address Book',
  'assets': 'Assets',
  'service-records': 'Service Records',
  'user-management': 'User Management',
};

// SUB_LABELS alias kept for readability below
const SUB_LABELS = SUB_LABELS_ALL;

// "Seen" invoice ids, per carrier, persisted in localStorage -- the
// Invoices tab badge should only flag invoices accounting hasn't opened
// the tab to see yet, not the full invoice count forever (a released
// load landing in Invoices from dispatch is "new" until accounting
// looks; a fully reviewed list shouldn't keep nagging).
const seenInvoicesKey = (cid) => `iv_seen_${cid}`;
const getSeenInvoiceIds = (cid) => {
  try { return new Set(JSON.parse(localStorage.getItem(seenInvoicesKey(cid)) || '[]')); }
  catch { return new Set(); }
};
const markInvoicesSeen = (cid, ids) => {
  try { localStorage.setItem(seenInvoicesKey(cid), JSON.stringify(ids)); } catch { /* ignore */ }
};

export default function AdminPanel() {
  const { user, staffRole, carrierPlan, carrierId } = useAuth();
  const navigate = useNavigate();
  const [loads, setLoads] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [settlements, setSettlements] = useState([]);
  // Same CompanySettings key/default ActiveLoad.jsx uses when it finalizes
  // a per-load Settlement -- feeds the Carrier Take KPI's fleet-wide
  // estimate below, since most loads never get an actual Settlement row.
  const [carrierTakePercent, setCarrierTakePercent] = useState(15);
  const [invoicedLoadIds, setInvoicedLoadIds] = useState(new Set());
  // Invoices tab badge -- counts only invoices accounting hasn't opened
  // the tab to see yet (persisted per-carrier in localStorage), not the
  // full invoice count. Otherwise a fully reviewed/reconciled invoice
  // list still shows a badge forever just because invoices exist.
  const [unseenInvoiceCount, setUnseenInvoiceCount] = useState(0);
  const [invoicesForBadge, setInvoicesForBadge] = useState([]);
  // Feeds the department-scoped KPI row below -- HR's stats (headcount,
  // approvals) come from these, not from anything dispatch/accounting use.
  const [driverRoster, setDriverRoster] = useState([]);
  const [staffRoster, setStaffRoster] = useState([]);
  // Resolves the Loads table's Trip # column to the real linked Trip
  // (via trip_loads), not just the legacy free-text loads.trip_number.
  const [tripLoadLinks, setTripLoadLinks] = useState([]);
  const [tripsById, setTripsById] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [showNewLoad, setShowNewLoad] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [chatLoad, setChatLoad] = useState(null);
  const [editLoad, setEditLoad] = useState(null);
  const [reviewSettlement, setReviewSettlement] = useState(null);
  const [approveAdvance, setApproveAdvance] = useState(null);
  const [timeFilter, setTimeFilter] = useState('all_time');
  const [loadStatusFilter, setLoadStatusFilter] = useState('All');
  const [loadTimeFilter, setLoadTimeFilter] = useState('All');
  const [loadsView, setLoadsView] = useState('table'); // 'table' | 'cards'
  // Click Pickup/Delivery column header to sort by that date; click again to flip direction.
  // Defaults to Delivery date, most recent first, on landing in Dispatch.
  const [dateSort, setDateSort] = useState({ key: 'delivery_date', dir: -1 }); // key: 'pickup_date' | 'delivery_date' | null

  // Loads table (TABLE VIEW): a mirrored horizontal scrollbar rendered
  // above the table, in sync with the browser's native one at the
  // bottom -- the table has enough columns that the bottom scrollbar
  // sits well below the fold on a long list, so sideways-scrolling
  // meant scrolling all the way down first just to reach it.
  const loadsTableRef = useRef(null);
  const loadsTopScrollRef = useRef(null);
  const loadsBottomScrollRef = useRef(null);
  const [loadsTableScrollWidth, setLoadsTableScrollWidth] = useState(0);
  const loadsScrollSyncRef = useRef(false);
  useEffect(() => {
    const el = loadsTableRef.current;
    if (!el) return;
    // Observes the <table> element itself (not the overflow-x-auto
    // wrapper) -- the wrapper's own box never changes size once
    // clipped, but the table's true rendered width does whenever the
    // data changes (e.g. a longer customer name widening that column).
    const ro = new ResizeObserver(() => setLoadsTableScrollWidth(el.scrollWidth));
    ro.observe(el);
    setLoadsTableScrollWidth(el.scrollWidth);
    return () => ro.disconnect();
  }, [loadsView]);
  const handleLoadsTopScroll = () => {
    if (loadsScrollSyncRef.current) { loadsScrollSyncRef.current = false; return; }
    loadsScrollSyncRef.current = true;
    if (loadsBottomScrollRef.current && loadsTopScrollRef.current) {
      loadsBottomScrollRef.current.scrollLeft = loadsTopScrollRef.current.scrollLeft;
    }
  };
  const handleLoadsBottomScroll = () => {
    if (loadsScrollSyncRef.current) { loadsScrollSyncRef.current = false; return; }
    loadsScrollSyncRef.current = true;
    if (loadsTopScrollRef.current && loadsBottomScrollRef.current) {
      loadsTopScrollRef.current.scrollLeft = loadsBottomScrollRef.current.scrollLeft;
    }
  };

  // Per-column pixel widths for the Loads table, user-resizable via a drag
  // handle on each header's right edge. Sums to ~1250px -- fits a normal
  // desktop viewport with no scroll at the defaults (the table's natural
  // width under table-fixed IS the sum of these, so the existing
  // overflow-x-auto wrapper scrolls it exactly when that sum exceeds the
  // container -- on a phone at the defaults, or on any screen once a
  // column's been dragged wider than what's left).
  const DEFAULT_LOADS_COL_WIDTHS = {
    load: 100, status: 90, pickup: 90, delivery: 90, driver: 120, truck: 85,
    customer: 120, from: 110, to: 110, bol: 70, total: 85, actions: 180,
  };
  const MIN_LOADS_COL_WIDTH = 50;
  const [loadsColWidths, setLoadsColWidths] = useState(DEFAULT_LOADS_COL_WIDTHS);
  const loadsResizeRef = useRef(null); // { key, startX, startWidth } while dragging

  const handleLoadsColResizeMove = (e) => {
    const r = loadsResizeRef.current;
    if (!r) return;
    const delta = e.clientX - r.startX;
    setLoadsColWidths(w => ({ ...w, [r.key]: Math.max(MIN_LOADS_COL_WIDTH, r.startWidth + delta) }));
  };
  const handleLoadsColResizeEnd = () => {
    loadsResizeRef.current = null;
    window.removeEventListener('pointermove', handleLoadsColResizeMove);
    window.removeEventListener('pointerup', handleLoadsColResizeEnd);
  };
  const startLoadsColResize = (key) => (e) => {
    e.preventDefault();
    e.stopPropagation(); // don't trigger a sortable header's own onClick
    loadsResizeRef.current = { key, startX: e.clientX, startWidth: loadsColWidths[key] };
    window.addEventListener('pointermove', handleLoadsColResizeMove);
    window.addEventListener('pointerup', handleLoadsColResizeEnd);
  };
  // A draggable handle on a header's right edge. Absolutely positioned so
  // it doesn't take up any of the column's own width/layout. Wider hit
  // target (16px) than the visible bar (2px) -- an 8px-wide target was
  // hard to actually land a mouse/trackpad on, especially with a
  // hover-only indicator giving no clue where to aim before you're
  // already on it. touch-action: none stops a touch-drag here from also
  // scrolling the page.
  const ColResizeHandle = ({ colKey }) => (
    <div
      onPointerDown={startLoadsColResize(colKey)}
      className="absolute top-0 right-0 h-full w-4 cursor-col-resize -mr-2 z-10 group flex justify-center"
      style={{ touchAction: 'none' }}
      title="Drag to resize"
    >
      <div className="w-[2px] h-full bg-border group-hover:bg-primary group-hover:w-[3px] transition-all" />
    </div>
  );
  const getDefaultParent = (role) => {
    const allowed = ROLE_TAB_ACCESS[role] || [];
    return allowed[0] || 'dispatch';
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeParent, setActiveParent] = useState(() => getDefaultParent(null));
  const [activeTab, setActiveTab] = useState('loads');
  const [detailLoad, setDetailLoad] = useState(null);
  const [maintenanceAssets, setMaintenanceAssets] = useState([]);
  const [maintenanceRecords, setMaintenanceRecords] = useState([]);
  const [maintenanceAlerts, setMaintenanceAlerts] = useState([]);
  const [dqfTargetDriverId, setDqfTargetDriverId] = useState(null);
  const [assetTargetId, setAssetTargetId] = useState(null);
  // Payroll History lives inside the Payroll tab as an inner view now,
  // not its own top-level Accounting sub-tab.
  const [payrollView, setPayrollView] = useState('run'); // 'run' | 'history'

  // Filter tabs based on role, then by plan (Owner-Op sees a trimmed set --
  // see OWNER_OP_BLOCKED_PARENTS / OWNER_OP_REPORT_SUBS above).
  const PARENT_TABS = ALL_PARENT_TABS
    .filter(p => {
      const allowed = ROLE_TAB_ACCESS[staffRole] || [];
      if (!allowed.includes(p.id)) return false;
      if (carrierPlan === 'owner_op' && OWNER_OP_BLOCKED_PARENTS.includes(p.id)) return false;
      return true;
    })
    .map(p => {
      if (p.id === 'accounting' && carrierPlan === 'owner_op') {
        // Owner-operators pay themselves per load -- no multi-driver
        // payroll BATCH to run, so 'payroll' (Run Payroll + History)
        // stays hidden. 'settlements' (their own per-load pay record,
        // labeled "Driver Pay Mgt" below) DOES apply -- it's how they
        // see what they earned -- and was wrongly filtered out
        // alongside payroll originally. 'pay-settings' (Insurance/ELD
        // deductions, Carrier Take %) is also hidden -- those configure
        // what the carrier deducts from OTHER drivers it pays; a
        // single-truck O/O has no one else to deduct against. 'tax-forms'
        // (W-2/W-4 compliance) is hidden too -- an O/O is a 1099
        // contractor themselves with no W-2 employees to track.
        return { ...p, subs: p.subs.filter(s => !['payroll', 'pay-settings', 'tax-forms'].includes(s)) };
      }
      if (p.id === 'reports' && carrierPlan === 'owner_op') {
        return { ...p, subs: p.subs.filter(s => OWNER_OP_REPORT_SUBS.includes(s)) };
      }
      return p;
    });

  // Single path for changing tabs programmatically (as opposed to the
  // sidebar's own <Link to="/admin?section=..."> clicks) -- pass `subId`
  // to land on a specific sub-tab instead of the parent's default first
  // one. Keeps the `?section=` URL param in sync with activeParent so a
  // refresh (or the sidebar's own active-item highlighting) doesn't go
  // stale after a programmatic jump like "Create Invoice" on a load.
  const switchParent = (parentId, subId) => {
    const parent = PARENT_TABS.find(p => p.id === parentId);
    if (!parent) {
      // Plan doesn't include this section (e.g. Maintenance for an
      // Owner-Operator) -- land on the default tab instead of a blocked
      // one with an empty sub-nav. Can happen via a stale onClick handler
      // (e.g. a Dashboard card built before a plan downgrade).
      const fallback = getDefaultParent(staffRole);
      const fp = PARENT_TABS.find(p => p.id === fallback);
      setActiveParent(fallback);
      setActiveTab(fp?.subs?.[0] || fallback);
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('section', fallback);
        return next;
      }, { replace: true });
      return;
    }
    setActiveParent(parentId);
    if (subId) {
      setActiveTab(subId);
    } else if (parent?.subs?.length > 0) {
      setActiveTab(parent.subs[0]);
    } else {
      setActiveTab(parentId);
    }
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('section', parentId);
      return next;
    }, { replace: true });
  };


  // SECURITY: Hard-redirect users who have no staff role
  useEffect(() => {
    if (user && !staffRole) {
      toast.error("Access denied.");
      navigate('/');
    }
    if (staffRole) {
      const section = searchParams.get('section');
      // Validate against the plan-filtered PARENT_TABS (not just the raw
      // role list) so a manually-typed ?section=maintenance can't reach a
      // tab an Owner-Operator's plan doesn't include.
      const allowed = PARENT_TABS.map(p => p.id);
      const targetParent = (section && allowed.includes(section)) ? section : getDefaultParent(staffRole);
      // switchParent() also writes `?section=` (to keep the URL in sync
      // after a programmatic jump like "Create Invoice"), which re-fires
      // this effect via the searchParams dependency. Only reset activeTab
      // to the parent's default sub when the parent actually changed from
      // an external source (sidebar Link, fresh load, back/forward) --
      // otherwise this would stomp a specific sub-tab switchParent(parent,
      // subId) just chose back to parent.subs[0] on every call.
      setActiveParent(prevParent => {
        if (prevParent === targetParent) return prevParent;
        const parent = PARENT_TABS.find(p => p.id === targetParent);
        setActiveTab(parent?.subs?.[0] || targetParent);
        return targetParent;
      });
    }
  }, [user, staffRole, carrierPlan, navigate, searchParams]);

  useEffect(() => {
    if (staffRole) {
      loadAll();
      loadMaintenanceData();
    }
  }, [staffRole]);

  // Opening the Invoices tab is what "reviewing" means for the badge --
  // mark every invoice currently loaded as seen, so the badge only ever
  // flags what's landed since the last time someone actually looked.
  // Re-runs whenever invoicesForBadge changes (a fresh Invoice.create/
  // delete/payment triggers loadAll()), so a new invoice arriving while
  // the tab is already open gets marked seen too instead of nagging on
  // the next visit.
  useEffect(() => {
    if (activeTab === 'broker-invoices' && carrierId && invoicesForBadge.length >= 0) {
      markInvoicesSeen(carrierId, invoicesForBadge.map(i => i.id));
      setUnseenInvoiceCount(0);
    }
  }, [activeTab, carrierId, invoicesForBadge]);

  const loadMaintenanceData = async () => {
    const [assets, records] = await Promise.all([
      client.entities.Asset.list('unit_number', 200),
      client.entities.MaintenanceRecord.list('-service_date', 500),
    ]);
    setMaintenanceAssets(assets);
    setMaintenanceRecords(records);

    const today = new Date();
    const alerts = [];
    assets.forEach(a => {
      const checks = [
        { label: `Unit ${a.unit_number} — Plate expires`, date: a.plate_expiration },
        { label: `Unit ${a.unit_number} — Registration expires`, date: a.registration_expiration },
        { label: `Unit ${a.unit_number} — Annual inspection due`, date: a.annual_inspection_due },
      ];
      checks.forEach(({ label, date }) => {
        if (!date) return;
        const daysUntil = Math.ceil((new Date(date) - today) / 86400000);
        if (daysUntil <= 30) alerts.push({ label, date, daysUntil, severity: daysUntil <= 0 ? 'critical' : daysUntil <= 7 ? 'high' : 'medium' });
      });
    });
    records.forEach(r => {
      if (r.next_service_date) {
        const daysUntil = Math.ceil((new Date(r.next_service_date) - today) / 86400000);
        if (daysUntil <= 14) alerts.push({ label: `Unit ${r.unit_number} — ${r.service_type} due`, date: r.next_service_date, daysUntil, severity: daysUntil <= 0 ? 'critical' : daysUntil <= 7 ? 'high' : 'medium' });
      }
      if (r.next_service_miles) {
        const asset = assets.find(a => a.id === r.asset_id);
        if (asset) {
          const milesUntil = r.next_service_miles - (asset.current_odometer || 0);
          if (milesUntil <= 1500) alerts.push({ label: `Unit ${asset.unit_number} — ${r.service_type} due in ${milesUntil} miles`, daysUntil: null, severity: milesUntil <= 0 ? 'critical' : milesUntil <= 500 ? 'high' : 'medium' });
        }
      }
    });
    setMaintenanceAlerts(alerts);

    // Toast critical alerts on load
    alerts.filter(a => a.severity === 'critical').forEach(a => {
      toast.error(`🔴 ${a.label}${a.date ? ` — ${a.date}` : ''}`);
    });
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [ls, adv, sett, inv, driverList, staffList, tripLoadList, tripList, companySettings] = await Promise.all([
        client.entities.Load.list('-created_date', 100).catch(() => []),
        client.entities.FuelAdvance.list('-created_date', 50).catch(() => []),
        client.entities.Settlement.list('-created_date', 200).catch(() => []),
        client.entities.Invoice.list('-created_date', 1000).catch(() => []),
        client.entities.DriverProfile.list('-created_date', 200).catch(() => []),
        client.entities.StaffProfile.list('-created_date', 200).catch(() => []),
        client.entities.TripLoad.list('-created_date', 500).catch(() => []),
        client.entities.Trip.list('-created_date', 300).catch(() => []),
        client.entities.CompanySettings.filter({ key: 'carrier_take_percent' }).catch(() => []),
      ]);
      setLoads(ls);
      setAdvances(adv);
      setSettlements(sett);
      const carrierSetting = companySettings.find(s => s.key === 'carrier_take_percent');
      // Was `parseFloat(x) || 15` -- indistinguishable from "not set" if a
      // carrier ever deliberately configured 0%, silently forcing 15%
      // back on.
      setCarrierTakePercent(numOrDefault(carrierSetting?.value, 15));
      setInvoicedLoadIds(new Set(inv.map(i => i.load_id).filter(Boolean)));
      setInvoicesForBadge(inv);
      setDriverRoster(driverList);
      setStaffRoster(staffList);
      setTripLoadLinks(tripLoadList);
      setTripsById(new Map(tripList.map(t => [t.id, t])));
      const seen = getSeenInvoiceIds(carrierId);
      setUnseenInvoiceCount(inv.filter(i => !seen.has(i.id)).length);
    } catch (e) {
      // ignore — individual .catch(() => []) handles each failure
    } finally {
      setLoading(false);
    }
  };



  // The explicit dispatcher action that was missing entirely: a Settlement
  // (and the Invoice draft) already exist the moment a load is delivered,
  // but nothing required dispatch to actually confirm/release it before
  // accounting could approve and pay it -- a load could sit as
  // 'Delivered' while its settlement was already fully paid out, with no
  // dispatch action in between. This flips the load to 'Invoiced', which
  // the Driver Settlement tab's Review & Approve gate below now requires.
  const handleReleaseToAccounting = async (load) => {
    if (!window.confirm(`Release Load #${load.load_number} to Accounting? This confirms it's ready for settlement review and payroll.`)) return;
    await client.entities.Load.update(load.id, { status: 'Invoiced' });
    toast.success(`Load #${load.load_number} released to Accounting.`);
    loadAll();
  };

  const handleAdvanceAction = async (adv, status) => {
    const update = { status };
    if (status === 'Approved') update.approved_date = new Date().toISOString().split('T')[0];
    await client.entities.FuelAdvance.update(adv.id, update);
    toast.success(`${adv.advance_type === 'personal' ? 'Personal' : 'Fuel'} advance ${status.toLowerCase()}.`);
    loadAll();
  };

  // Personal advances aren't tied to a load, so the fuel-specific approval
  // modal (MoneyCode, load-chat message) doesn't apply -- a straight
  // approve + a simple confirmation email is enough. Deducted at the next
  // payroll run (PayrollTab.jsx), not here.
  const handleApprovePersonal = async (adv) => {
    await handleAdvanceAction(adv, 'Approved');
    if (adv.driver_email) {
      try {
        await client.integrations.Core.SendEmail({
          to: adv.driver_email,
          subject: 'Personal Advance Approved',
          body: `Hi ${adv.driver_name},<br><br>Your personal advance request of $${adv.amount?.toFixed(2)} has been approved. It will be deducted from your pay at the next payroll run.<br><br>Thank you.`,
        });
      } catch { /* email failure is non-blocking */ }
    }
  };

  const handleRevertSettlement = async (s) => {
    if (!window.confirm(`Revert settlement #${s.load_number} back to Pending? YTD will NOT be reversed.`)) return;
    await client.entities.Settlement.update(s.id, {
      status: 'Pending',
      is_finalized: false,
      pdf_url: null,
      finalized_date: null,
      final_net_pay: null,
    });
    toast.success('Settlement reverted to Pending.');
    loadAll();
  };

  const handleRevertPaid = async (s) => {
    if (!window.confirm(`Revert paid settlement #${s.load_number} back to Approved? Payment reference will be cleared.`)) return;
    await client.entities.Settlement.update(s.id, {
      status: 'Approved',
      payment_reference: null,
      pdf_url: null,
    });
    toast.success('Settlement reverted to Approved.');
    loadAll();
  };

  const handleOpenReview = (s) => {
    setReviewSettlement(s);
  };

  if (!user || !staffRole) {
    return null;
  }

  // Time filter helper
  const getFilterStart = () => {
    const now = new Date();
    if (timeFilter === 'this_week') {
      const d = new Date(now);
      d.setDate(d.getDate() - d.getDay());
      d.setHours(0, 0, 0, 0);
      return d;
    }
    if (timeFilter === 'this_month') return new Date(now.getFullYear(), now.getMonth(), 1);
    if (timeFilter === 'this_year') return new Date(now.getFullYear(), 0, 1);
    return null; // all_time
  };

  const inPeriod = (dateStr) => {
    const start = getFilterStart();
    if (!start || !dateStr) return true;
    return new Date(dateStr) >= start;
  };

  // Filter settlements by period — use finalized_date for finalized, created_date for others
  const filteredSettlements = settlements.filter(s => {
    const dateToCheck = s.is_finalized ? s.finalized_date : s.created_date;
    return inPeriod(dateToCheck);
  });

  // A Settlement (and its Invoice draft) get created the moment a load is
  // delivered -- before dispatch has confirmed anything. Gates Review &
  // Approve below on dispatch actually having released the load (flipped
  // it past 'Delivered', see handleReleaseToAccounting). Unknown/missing
  // loads default to NOT gated, so old data with no linked load isn't
  // stuck unapprovable forever.
  const loadStatusById = new Map(loads.map(l => [l.id, l.status]));
  const isReleasedToAccounting = (s) => loadStatusById.get(s.load_id) !== 'Delivered';

  // Filter loads by created_date for the period
  const filteredLoads = loads.filter(l => inPeriod(l.created_date));

  // Stats
  // Fleet Revenue = gross revenue, which is just Load.rate -- available the
  // moment a load is booked, independent of whether it was ever processed
  // through a driver Settlement. Summing Settlement.gross_revenue instead
  // undercounted badly: Settlement rows only exist for loads that went
  // through ActiveLoad.jsx's driver-delivery flow, so CSV-imported and
  // manually-entered loads (the majority of this carrier's data) were
  // invisible to this KPI even though they're real, completed, billed loads.
  const completedLoads = filteredLoads.filter(l => ['Delivered', 'Invoiced'].includes(l.status));
  const totalRevenue = completedLoads.reduce((sum, l) => sum + (l.rate || 0), 0);
  // Carrier Take used to sum actual Settlement rows only (carrier_take_amount
  // + dispatch_fee_amount, finalized settlements only) -- same undercounting
  // bug as Fleet Revenue, just worse: only 5 of this carrier's 48 completed
  // loads have ever been processed through a Settlement, so the real number
  // ($2,619) looked absurd next to a $159k gross revenue figure. Estimated
  // the same way ActiveLoad.jsx computes it per-load at delivery time --
  // gross revenue x the carrier's take percent -- applied across every
  // completed load instead of only the handful with a Settlement record.
  const totalCarrierTake = totalRevenue * (carrierTakePercent / 100);
  const pendingAdvances = advances.filter(a => a.status === 'Pending');
  const pendingSettlements = settlements.filter(s => s.status === 'Pending');
  const deliveredLoads = loads.filter(l => l.status === 'Delivered');
  const criticalMaintenanceCount = maintenanceAlerts.filter(a => a.severity === 'critical' || a.severity === 'high').length;

  // Department-scoped KPI inputs -- HR shouldn't see Fleet Revenue/Carrier
  // Take, dispatch/accounting don't need headcount, etc. Each parent tab
  // gets its own small set instead of one static all-departments row.
  const availableLoadsCount = filteredLoads.filter(l => l.status === 'Available').length;
  const inTransitLoadsCount = filteredLoads.filter(l => l.status === 'In Transit').length;
  const activeDriversCount = driverRoster.filter(d => d.is_active !== false).length;
  const activeStaffCount = staffRoster.filter(s => s.is_active !== false).length;
  const pendingApprovalsCount = driverRoster.filter(d => d.approval_status === 'Pending').length;
  const newHires30dCount = driverRoster.filter(d => {
    if (!d.hire_date) return false;
    return (Date.now() - new Date(d.hire_date).getTime()) <= 30 * 24 * 60 * 60 * 1000;
  }).length;
  // Partial counts as still-owed here too -- otherwise a partially-paid
  // invoice's remaining balance silently drops out of this KPI.
  const unpaidInvoices = invoicesForBadge.filter(i => i.status === 'Unpaid' || i.status === 'Partial');
  const unpaidInvoicesBalance = unpaidInvoices.reduce((sum, i) => sum + (i.balance_due ?? i.total_amount ?? 0), 0);
  const paidInvoicesCount = invoicesForBadge.filter(i => i.status === 'Paid').length;
  const pendingSettlementsTotal = pendingSettlements.reduce((sum, s) => sum + (s.final_net_pay ?? s.net_driver_pay ?? 0), 0);
  const pendingAdvancesTotal = pendingAdvances.reduce((sum, a) => sum + (a.amount || 0), 0);

  const maintenanceKpi = { icon: Truck, label: 'Maintenance Alerts', value: maintenanceAlerts.length, color: criticalMaintenanceCount > 0 ? 'text-red-400' : maintenanceAlerts.length > 0 ? 'text-amber-400' : 'text-green-400', bg: criticalMaintenanceCount > 0 ? 'bg-red-500/10' : maintenanceAlerts.length > 0 ? 'bg-amber-500/10' : 'bg-green-500/10', onClick: () => switchParent('maintenance') };

  const KPIS_BY_PARENT = {
    dispatch: [
      { icon: Truck, label: 'Total Loads', value: filteredLoads.length, color: 'text-primary', bg: 'bg-primary/10' },
      { icon: PackageCheck, label: 'Available', value: availableLoadsCount, color: 'text-green-400', bg: 'bg-green-500/10' },
      { icon: Truck, label: 'In Transit', value: inTransitLoadsCount, color: 'text-blue-400', bg: 'bg-blue-500/10' },
      { icon: FileCheck, label: 'Awaiting Invoice', value: deliveredLoads.length, color: 'text-amber-400', bg: 'bg-amber-500/10', onClick: () => switchParent('accounting', 'broker-invoices') },
      maintenanceKpi,
    ],
    hr: [
      { icon: Users, label: 'Active Drivers', value: activeDriversCount, color: 'text-primary', bg: 'bg-primary/10' },
      { icon: Users, label: 'Active Staff', value: activeStaffCount, color: 'text-blue-400', bg: 'bg-blue-500/10' },
      { icon: ClipboardCheck, label: 'Pending Approvals', value: pendingApprovalsCount, color: pendingApprovalsCount > 0 ? 'text-amber-400' : 'text-green-400', bg: pendingApprovalsCount > 0 ? 'bg-amber-500/10' : 'bg-green-500/10', onClick: () => switchParent('hr', 'approvals') },
      { icon: UserPlus, label: 'New Hires (30d)', value: newHires30dCount, color: 'text-green-400', bg: 'bg-green-500/10' },
    ],
    accounting: [
      { icon: FileText, label: 'Unpaid Invoices', value: unpaidInvoices.length, sub: `$${unpaidInvoicesBalance.toLocaleString('en-US', { maximumFractionDigits: 0 })}`, color: 'text-amber-400', bg: 'bg-amber-500/10', onClick: () => switchParent('accounting', 'broker-invoices') },
      { icon: Check, label: 'Paid Invoices', value: paidInvoicesCount, color: 'text-green-400', bg: 'bg-green-500/10', onClick: () => switchParent('accounting', 'broker-invoices') },
      { icon: Clock, label: 'Pending Settlements', value: pendingSettlements.length, sub: `$${pendingSettlementsTotal.toLocaleString('en-US', { maximumFractionDigits: 0 })}`, color: 'text-blue-400', bg: 'bg-blue-500/10', onClick: () => switchParent('accounting', 'settlements') },
      { icon: Fuel, label: 'Pending Advances', value: pendingAdvances.length, sub: pendingAdvancesTotal > 0 ? `$${pendingAdvancesTotal.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : undefined, color: 'text-amber-400', bg: 'bg-amber-500/10', onClick: () => switchParent('accounting', 'advances') },
      { icon: DollarSign, label: 'Fleet Revenue', value: `$${totalRevenue.toLocaleString()}`, color: 'text-green-400', bg: 'bg-green-500/10' },
    ],
    // reports/maintenance/settings (super_admin only) -- no dedicated
    // department, keep the full picture super_admin already had.
    default: [
      { icon: Truck, label: 'Total Loads', value: filteredLoads.length, color: 'text-primary', bg: 'bg-primary/10' },
      { icon: DollarSign, label: 'Fleet Revenue', value: `$${totalRevenue.toLocaleString()}`, color: 'text-green-400', bg: 'bg-green-500/10' },
      { icon: Fuel, label: 'Pending Advances', value: pendingAdvances.length, color: 'text-amber-400', bg: 'bg-amber-500/10' },
      { icon: BarChart3, label: 'Carrier Take', value: `$${totalCarrierTake.toLocaleString()}`, color: 'text-blue-400', bg: 'bg-blue-500/10' },
      maintenanceKpi,
    ],
  };
  const activeKpis = KPIS_BY_PARENT[activeParent] || KPIS_BY_PARENT.default;

  const TIME_OPTIONS = [
    { value: 'this_week', label: 'This Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'this_year', label: 'This Year' },
    { value: 'all_time', label: 'All Time' },
  ];



  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-space text-2xl font-bold text-foreground">Admin Panel</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {staffRole === 'super_admin' && 'Full access — all departments'}
            {staffRole === 'dispatch' && 'Dispatch — post loads, manage fleet'}
            {staffRole === 'hr' && 'HR — drivers and approvals'}
            {staffRole === 'accounting' && 'Accounting — payroll and invoices'}
          </p>
        </div>
        {/* Time Filter */}
        <div className="flex items-center gap-2 glass-card px-3 py-2 rounded-xl">
          <Calendar className="w-4 h-4 text-primary flex-shrink-0" />
          <select
            value={timeFilter}
            onChange={e => setTimeFilter(e.target.value)}
            className="bg-transparent text-foreground text-sm font-medium focus:outline-none cursor-pointer pr-1"
          >
            {TIME_OPTIONS.map(o => (
              <option key={o.value} value={o.value} className="bg-background text-foreground">{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Expiry Alerts */}
      <ExpiryAlertBanner
        onOpenDriver={(id) => { switchParent('hr', 'drivers'); setDqfTargetDriverId(id); }}
        onOpenAsset={(id) => { switchParent('maintenance', 'assets'); setAssetTargetId(id); }}
      />

      {/* Stats -- scoped to whichever department (activeParent) is open */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {activeKpis.map(s => (
          <div key={s.label} className={cn("glass-card p-5 flex flex-col items-center text-center", s.onClick && "cursor-pointer hover:brightness-110 transition-all")} onClick={s.onClick}>
            <div className={cn("w-9 h-9 rounded-xl flex items-center justify-center mb-3", s.bg)}>
              <s.icon className={cn("w-4 h-4", s.color)} />
            </div>
            <p className="text-xl font-space font-bold text-foreground">{s.value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
            {s.sub && <p className="text-xs text-muted-foreground/70 mt-0.5">{s.sub}</p>}
          </div>
        ))}
      </div>

      {/* Sub-tabs navigation -- skipped for parents with no subs (Dashboard) */}
      {(PARENT_TABS.find(p => p.id === activeParent)?.subs.length > 0) && (
        <div className="flex overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] border border-border rounded-xl px-3 gap-0 bg-white/3">
            {PARENT_TABS.find(p => p.id === activeParent)?.subs.map(sub => {
              const isActive = activeTab === sub;
              const badge =
                sub === 'advances' && pendingAdvances.length > 0 ? pendingAdvances.length :
                sub === 'settlements' && pendingSettlements.length > 0 ? pendingSettlements.length :
                sub === 'loads' && deliveredLoads.length > 0 ? deliveredLoads.length :
                sub === 'broker-invoices' && unseenInvoiceCount > 0 ? unseenInvoiceCount :
                (sub === 'assets' || sub === 'service-records') && maintenanceAlerts.length > 0 ? maintenanceAlerts.length :
                null;
              return (
                <button
                  key={sub}
                  onClick={() => setActiveTab(sub)}
                  className={cn(
                    "whitespace-nowrap shrink-0 flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2",
                    isActive
                      ? "border-foreground text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  )}
                >
                  {sub === 'map' && <MapIcon className="w-3.5 h-3.5" />}
                  {SUB_LABELS[sub]}
                  {badge && (
                    <span className="ml-1 bg-primary text-primary-foreground text-xs font-bold px-1.5 py-0.5 rounded-full leading-none">
                      {badge}
                    </span>
                  )}
                </button>
              );
            })}
        </div>
      )}

      {/* ── Tab Content ── */}
      <div className="mt-4">
      {/* Wrapped as a fake "Tabs" block for content rendering */}

        {/* ── DASHBOARD TAB ── */}
        {activeTab === 'dashboard' && (
          <HomeDashboard
            onOpenLoads={(status) => {
              switchParent('dispatch', 'loads');
              setLoadStatusFilter(status && status !== '__late' ? status : 'All');
            }}
            onOpenDriver={() => switchParent('hr', 'drivers')}
            onOpenAsset={() => switchParent('maintenance', 'assets')}
            onOpenInvoices={() => switchParent('accounting', 'broker-invoices')}
          />
        )}

        {/* ── LOADS TAB ── */}
        {activeTab === 'loads' && (() => {
          const today = new Date().toISOString().split('T')[0];

          const getPickupTiming = (l) => {
            if (!l.pickup_date) return 'past';
            if (l.pickup_date > today) return 'future';
            if (l.pickup_date === today) return 'today';
            return 'past';
          };

          // Row color is keyed to DELIVERY date, not pickup -- dispatch cares
          // when a load needs to land, not when it left. Status already has
          // its own colored badge in the Status column, so this doesn't
          // also tint by status; a load past its delivery date gets no
          // color at all (nothing dispatch-actionable left to flag).
          const getDeliveryTiming = (l) => {
            if (!l.delivery_date) return 'past';
            if (l.delivery_date > today) return 'future';
            if (l.delivery_date === today) return 'today';
            return 'past';
          };

          const getRowBg = (l) => {
            const t = getDeliveryTiming(l);
            if (t === 'future') return 'bg-yellow-500/15';
            if (t === 'today') return 'bg-green-500/15';
            return '';
          };

          const visibleLoads = loads.filter(l => {
            const statusOk = loadStatusFilter === 'All' || l.status === loadStatusFilter;
            const timeOk = loadTimeFilter === 'All' ||
              (loadTimeFilter === 'Future' && getPickupTiming(l) === 'future') ||
              (loadTimeFilter === 'Today' && getPickupTiming(l) === 'today') ||
              (loadTimeFilter === 'Past' && getPickupTiming(l) === 'past');
            return statusOk && timeOk;
          });
          if (dateSort.key) {
            visibleLoads.sort((a, b) => {
              const av = a[dateSort.key] || '';
              const bv = b[dateSort.key] || '';
              // Blank dates always sink to the bottom regardless of direction.
              if (!av && !bv) return 0;
              if (!av) return 1;
              if (!bv) return -1;
              return dateSort.dir * av.localeCompare(bv);
            });
          }
          const toggleDateSort = (key) => {
            setDateSort(prev => prev.key === key ? { key, dir: prev.dir * -1 } : { key, dir: 1 });
          };

          // Truck column: whichever active Truck asset is currently
          // assigned to the load's driver (assets are assigned to
          // DRIVERS, not loads, so this is a live lookup, not a stored
          // field -- reassigning a driver's truck updates every load's
          // Truck column automatically instead of going stale).
          const truckUnitByDriverEmail = new Map(
            maintenanceAssets
              .filter(a => a.asset_type === 'Truck' && a.is_active !== false && a.assigned_driver_email)
              .map(a => [a.assigned_driver_email.toLowerCase(), a.unit_number])
          );
          // CSV-imported loads carry truck_unit as free text with no
          // connection to an actual Asset record -- if that text happens to
          // match a real Truck asset's unit_number (just not currently
          // assigned to this load's driver), resolve and format it the same
          // "Unit X" way as a live assignment instead of showing raw,
          // unverified text next to properly-resolved units.
          const truckUnitNumbers = new Set(
            maintenanceAssets.filter(a => a.asset_type === 'Truck').map(a => (a.unit_number || '').toLowerCase())
          );

          // Trip # column: the real linked Trip (via trip_loads) if this
          // load has been attached to one from the Trips tab -- falls
          // back to the legacy free-text loads.trip_number for loads
          // never linked to a real Trip.
          const tripNumberByLoadId = new Map(
            tripLoadLinks
              .map(link => [link.load_id, tripsById.get(link.trip_id)?.trip_number])
              .filter(([, tripNumber]) => tripNumber)
          );

          return (
            <div className="space-y-4">
              {/* Controls row */}
              <div className="flex flex-wrap justify-between items-start gap-3">
                <div className="flex flex-col gap-2">
                  {/* Status pills */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {['All', 'Available', 'Booked', 'In Transit', 'Delivered', 'Invoiced', 'Claim'].map(s => (
                      <button
                        key={s}
                        onClick={() => setLoadStatusFilter(s)}
                        className={cn(
                          "text-xs font-semibold px-3 py-1 rounded-full border transition-colors",
                          loadStatusFilter === s
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'border-border text-muted-foreground hover:text-foreground hover:border-border'
                        )}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  {/* Time pills */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {['All', 'Future', 'Today', 'Past'].map(t => (
                      <button
                        key={t}
                        onClick={() => setLoadTimeFilter(t)}
                        className={cn(
                          "text-xs font-semibold px-3 py-1 rounded-full border transition-colors",
                          loadTimeFilter === t
                            ? 'bg-secondary text-foreground border-border'
                            : 'border-border text-muted-foreground hover:text-foreground hover:border-border'
                        )}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* View toggle */}
                  <div className="flex items-center rounded-lg border border-border overflow-hidden">
                    <button
                      onClick={() => setLoadsView('table')}
                      className={cn("p-1.5 transition-colors", loadsView === 'table' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-white/10')}
                      title="Table view"
                    >
                      <List className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setLoadsView('cards')}
                      className={cn("p-1.5 transition-colors", loadsView === 'cards' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-white/10')}
                      title="Card view"
                    >
                      <LayoutGrid className="w-4 h-4" />
                    </button>
                  </div>
                  <Button onClick={() => setShowCsvImport(true)} variant="outline" className="border-border">
                    <Upload className="w-4 h-4 mr-2" /> Import CSV
                  </Button>
                  <Button onClick={() => setShowNewLoad(!showNewLoad)} className="bg-primary text-primary-foreground">
                    <Plus className="w-4 h-4 mr-2" /> Create Load
                  </Button>
                </div>
              </div>

              {/* Row-color legend -- matches getRowBg above */}
              <div className="flex items-center gap-3 flex-wrap text-xs">
                <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-yellow-500/15 text-yellow-700 dark:text-yellow-300 font-medium">
                  Loads delivered in the future
                </span>
                <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-green-500/15 text-green-700 dark:text-green-300 font-medium">
                  Loads delivered today
                </span>
                <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border text-muted-foreground font-medium">
                  Loads delivered in the past
                </span>
              </div>

              {showNewLoad && (
                <NewLoadModal onClose={() => setShowNewLoad(false)} onCreated={loadAll} />
              )}

              {showCsvImport && (
                <LoadsCsvImportModal onClose={() => setShowCsvImport(false)} onImported={loadAll} />
              )}

              {chatLoad && (
                <AdminLoadChat load={chatLoad} onClose={() => setChatLoad(null)} />
              )}

              {/* TABLE VIEW */}
              {loadsView === 'table' && (
                <div className="glass-card overflow-hidden">
                  {loadsTableScrollWidth > 0 && (
                    <div
                      ref={loadsTopScrollRef}
                      onScroll={handleLoadsTopScroll}
                      className="overflow-x-auto overflow-y-hidden border-b border-border"
                      style={{ height: 14 }}
                    >
                      <div style={{ width: loadsTableScrollWidth, height: 1 }} />
                    </div>
                  )}
                  <div className="overflow-x-auto" ref={loadsBottomScrollRef} onScroll={handleLoadsBottomScroll}>
                    {/* table-fixed + an explicit PIXEL width per column
                        (loadsColWidths), each with a drag handle on its
                        right edge -- table-fixed makes the table's natural
                        width exactly the sum of these, so it fits a normal
                        desktop viewport with no scroll at the defaults, and
                        the wrapper's overflow-x-auto scrolls it exactly
                        when that sum exceeds the container (a phone at the
                        defaults, or any screen once a column's been
                        dragged wider than what's left) -- same tradeoff
                        any resizable data table makes, instead of forcing
                        an exact fit that squeezes columns unreadably thin. */}
                    <table className="text-xs table-fixed" ref={loadsTableRef}>
                      <thead>
                        <tr className="border-b border-border">
                          <th style={{ width: loadsColWidths.load }} className="relative text-left px-3 py-2.5 text-muted-foreground font-medium">Load # <ColResizeHandle colKey="load" /></th>
                          <th style={{ width: loadsColWidths.status }} className="relative text-left px-3 py-2.5 text-muted-foreground font-medium">Status <ColResizeHandle colKey="status" /></th>
                          <th
                            onClick={() => toggleDateSort('pickup_date')}
                            style={{ width: loadsColWidths.pickup }}
                            className="relative text-left px-3 py-2.5 text-muted-foreground font-medium cursor-pointer hover:text-foreground select-none"
                          >
                            <span className="flex items-center gap-1">
                              Pickup
                              {dateSort.key === 'pickup_date'
                                ? (dateSort.dir === 1 ? <ArrowUp className="w-3 h-3 text-primary" /> : <ArrowDown className="w-3 h-3 text-primary" />)
                                : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                            </span>
                            <ColResizeHandle colKey="pickup" />
                          </th>
                          <th
                            onClick={() => toggleDateSort('delivery_date')}
                            style={{ width: loadsColWidths.delivery }}
                            className="relative text-left px-3 py-2.5 text-muted-foreground font-medium cursor-pointer hover:text-foreground select-none"
                          >
                            <span className="flex items-center gap-1">
                              Delivery
                              {dateSort.key === 'delivery_date'
                                ? (dateSort.dir === 1 ? <ArrowUp className="w-3 h-3 text-primary" /> : <ArrowDown className="w-3 h-3 text-primary" />)
                                : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                            </span>
                            <ColResizeHandle colKey="delivery" />
                          </th>
                          <th style={{ width: loadsColWidths.driver }} className="relative text-left px-3 py-2.5 text-muted-foreground font-medium">Driver <ColResizeHandle colKey="driver" /></th>
                          <th style={{ width: loadsColWidths.truck }} className="relative text-left px-3 py-2.5 text-muted-foreground font-medium">Truck <ColResizeHandle colKey="truck" /></th>
                          <th style={{ width: loadsColWidths.customer }} className="relative text-left px-3 py-2.5 text-muted-foreground font-medium">Customer <ColResizeHandle colKey="customer" /></th>
                          <th style={{ width: loadsColWidths.from }} className="relative text-left px-3 py-2.5 text-muted-foreground font-medium">From <ColResizeHandle colKey="from" /></th>
                          <th style={{ width: loadsColWidths.to }} className="relative text-left px-3 py-2.5 text-muted-foreground font-medium">To <ColResizeHandle colKey="to" /></th>
                          <th style={{ width: loadsColWidths.bol }} className="relative text-left px-3 py-2.5 text-muted-foreground font-medium">BOL <ColResizeHandle colKey="bol" /></th>
                          <th style={{ width: loadsColWidths.total }} className="relative text-right px-3 py-2.5 text-muted-foreground font-medium">Total <ColResizeHandle colKey="total" /></th>
                          <th style={{ width: loadsColWidths.actions }} className="relative text-right px-3 py-2.5 text-muted-foreground font-medium">Actions <ColResizeHandle colKey="actions" /></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {visibleLoads.map(l => (
                          <tr key={l.id} className={getRowBg(l)}>
                            <td className="px-3 py-2.5 cursor-pointer truncate" onClick={() => setDetailLoad(l)} title={`#${l.load_number}`}>
                              <span className="font-semibold text-blue-600 dark:text-blue-400 hover:underline">#{l.load_number}</span>
                              <div className="text-[10px] text-muted-foreground font-normal">Trip {tripNumberByLoadId.get(l.id) || l.trip_number || '—'}</div>
                            </td>
                            <td className="px-3 py-2.5">
                              <span className={cn(
                                "text-[10px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap",
                                l.status === 'Available' ? 'text-green-700 dark:text-green-400 bg-green-500/15 dark:bg-green-500/10 border-green-500/40 dark:border-green-500/30' :
                                l.status === 'Booked' ? 'text-amber-700 dark:text-amber-400 bg-amber-500/15 dark:bg-amber-500/10 border-amber-500/40 dark:border-amber-500/30' :
                                l.status === 'In Transit' ? 'text-blue-700 dark:text-blue-400 bg-blue-500/15 dark:bg-blue-500/10 border-blue-500/40 dark:border-blue-500/30' :
                                l.status === 'Invoiced' ? 'text-teal-700 dark:text-teal-400 bg-teal-500/15 dark:bg-teal-500/10 border-teal-500/40 dark:border-teal-500/30' :
                                l.status === 'Claim' ? 'text-red-700 dark:text-red-400 bg-red-500/15 dark:bg-red-500/10 border-red-500/40 dark:border-red-500/30' :
                                'text-purple-700 dark:text-purple-400 bg-purple-500/15 dark:bg-purple-500/10 border-purple-500/40 dark:border-purple-500/30'
                                )}>{l.status}</span>
                                </td>
                            <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{l.pickup_date || '—'}</td>
                            <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{l.delivery_date || '—'}</td>
                            <td className="px-3 py-2.5 text-foreground truncate" title={l.assigned_driver_name || 'Unassigned'}>{l.assigned_driver_name || <span className="text-muted-foreground italic">Unassigned</span>}</td>
                            <td className="px-3 py-2.5 text-muted-foreground truncate">
                              {(() => {
                                const unit = l.assigned_driver_email && truckUnitByDriverEmail.get(l.assigned_driver_email.toLowerCase());
                                if (unit) return `Unit ${unit}`;
                                // Falls back to the CSV-imported truck text when the
                                // driver has no live truck assignment to derive from
                                // (e.g. a historical load, or the driver wasn't matched
                                // to an existing DriverProfile at import time). If that
                                // text matches a real Truck asset's unit_number (just
                                // not currently assigned to this driver), format it the
                                // same "Unit X" way instead of showing raw text next to
                                // properly-resolved units -- otherwise show it as-is.
                                if (!l.truck_unit) return '—';
                                if (truckUnitNumbers.has(l.truck_unit.toLowerCase())) return `Unit ${l.truck_unit}`;
                                return l.truck_unit;
                              })()}
                            </td>
                            <td className="px-3 py-2.5 text-foreground truncate" title={l.customer_name || ''}>{l.customer_name || '—'}</td>
                            <td className="px-3 py-2.5 text-foreground truncate" title={l.origin || ''}>{l.origin || '—'}</td>
                            <td className="px-3 py-2.5 text-foreground truncate" title={l.destination || ''}>{l.destination || '—'}</td>
                            <td className="px-3 py-2.5 truncate">
                              {l.ebol_url ? (
                                <a href={l.ebol_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline" title="View eBOL">
                                  <ExternalLink className="w-3 h-3" /> View
                                </a>
                              ) : l.bol_number ? (
                                // CSV import brings a BOL NUMBER, not a document -- this
                                // app only ever had a place for an uploaded eBOL file
                                // before now, so show the number as plain text instead
                                // of silently discarding it.
                                <span className="text-muted-foreground">{l.bol_number}</span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-right font-semibold text-foreground whitespace-nowrap">${l.rate?.toLocaleString() ?? '—'}</td>
                                <td className="px-3 py-2.5 whitespace-nowrap">
                                <div className="flex items-center gap-1 justify-end">
                                <button onClick={() => setEditLoad(l)} className="p-1.5 rounded-lg border border-border bg-muted/40 text-foreground hover:bg-muted transition-colors" title="Edit">
                                 <Pencil className="w-3.5 h-3.5" />
                                </button>
                                {/* Chat drops off once the driver's job on this load is
                                    done -- nothing left to coordinate with them about. */}
                                {l.assigned_driver_name && !['Delivered', 'Invoiced', 'Claim'].includes(l.status) && (
                                 <button
                                   onClick={() => setChatLoad(chatLoad?.id === l.id ? null : l)}
                                   className={cn(
                                     "p-1.5 rounded-lg border transition-colors",
                                     chatLoad?.id === l.id
                                       ? "border-primary/40 bg-primary/10 text-primary"
                                       : "border-border bg-muted/40 text-foreground hover:bg-muted"
                                   )}
                                   title="Chat with Driver"
                                 >
                                   <MessageSquare className="w-3.5 h-3.5" />
                                 </button>
                                )}
                                {l.status === 'Delivered' && (
                                 <InvoiceDropdown
                                   hasInvoice={invoicedLoadIds.has(l.id)}
                                   onRelease={() => handleReleaseToAccounting(l)}
                                   onNavigate={() => switchParent('accounting', 'broker-invoices')}
                                 />
                                )}
                                </div>
                                </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {visibleLoads.length === 0 && <p className="text-center text-muted-foreground py-8 text-sm">No loads found.</p>}
                  </div>
                </div>
              )}

              {/* CARD VIEW */}
              {loadsView === 'cards' && (
                <div className="glass-card overflow-hidden">
                  <div className="divide-y divide-border">
                    {visibleLoads.map(l => (
                      <div key={l.id} className={cn("flex items-center justify-between px-5 py-3 gap-3", getRowBg(l))}>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-foreground truncate">#{l.load_number} — {l.origin} → {l.destination}</p>
                          <p className="text-xs text-muted-foreground">{l.equipment_required} · ${l.rate?.toLocaleString()} {l.assigned_driver_name && `· ${l.assigned_driver_name}`}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button onClick={() => setEditLoad(l)} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors" title="Edit Details">
                            <Pencil className="w-4 h-4" />
                          </button>
                          {l.assigned_driver_name && !['Delivered', 'Invoiced', 'Claim'].includes(l.status) && (
                            <button
                              onClick={() => setChatLoad(chatLoad?.id === l.id ? null : l)}
                              className={cn("p-1.5 rounded-lg transition-colors", chatLoad?.id === l.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-white/10")}
                              title="Chat with Driver"
                            >
                              <MessageSquare className="w-4 h-4" />
                            </button>
                          )}
                          <span className={cn(
                            "text-xs font-semibold px-2.5 py-0.5 rounded-full border whitespace-nowrap",
                            l.status === 'Available' ? 'text-green-700 dark:text-green-400 bg-green-500/15 dark:bg-green-500/10 border-green-500/40 dark:border-green-500/30' :
                            l.status === 'Booked' ? 'text-amber-700 dark:text-amber-400 bg-amber-500/15 dark:bg-amber-500/10 border-amber-500/40 dark:border-amber-500/30' :
                            l.status === 'In Transit' ? 'text-blue-700 dark:text-blue-400 bg-blue-500/15 dark:bg-blue-500/10 border-blue-500/40 dark:border-blue-500/30' :
                            'text-purple-700 dark:text-purple-400 bg-purple-500/15 dark:bg-purple-500/10 border-purple-500/40 dark:border-purple-500/30'
                          )}>{l.status}</span>
                        </div>
                      </div>
                    ))}
                    {visibleLoads.length === 0 && <p className="text-center text-muted-foreground py-8 text-sm">No loads found.</p>}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* ── TRIPS TAB ── */}
        {activeTab === 'trips' && <TripsTab />}

        {/* ── FUEL ADVANCES TAB ── */}
        {activeTab === 'advances' && <div>
          <div className="glass-card overflow-hidden">
            <div className="divide-y divide-border">
              {advances.map(a => (
                <div key={a.id} className="flex items-center justify-between px-5 py-4 gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground flex items-center gap-2 flex-wrap">
                      {a.driver_name} — <span className="text-primary">${a.amount?.toLocaleString()}</span>
                      <span className={cn(
                        "text-[10px] font-semibold px-1.5 py-0.5 rounded-full border",
                        a.advance_type === 'personal' ? 'text-purple-700 dark:text-purple-400 bg-purple-500/15 dark:bg-purple-500/10 border-purple-500/40 dark:border-purple-500/30' : 'text-primary bg-primary/10 border-primary/30'
                      )}>
                        {a.advance_type === 'personal' ? 'Personal' : 'Fuel'}
                      </span>
                    </p>
                    <div className="flex items-center gap-3 flex-wrap">
                      <p className="text-xs text-muted-foreground">
                        {a.load_number ? `Load #${a.load_number}` : 'Not tied to a load'} {a.approved_date && `· Approved ${a.approved_date}`}
                      </p>
                      {a.advance_type !== 'personal' && a.bol_url && (
                        <a
                          href={a.bol_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                        >
                          <ExternalLink className="w-3 h-3" /> View BOL
                        </a>
                      )}
                      {a.advance_type !== 'personal' && !a.bol_url && (
                        <span className="text-xs text-red-400/70">No BOL attached</span>
                      )}
                    </div>
                  </div>
                  {a.status === 'Pending' ? (
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        onClick={() => (a.advance_type === 'personal' ? handleApprovePersonal(a) : setApproveAdvance(a))}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground h-8 px-3"
                      >
                        <Check className="w-3 h-3 mr-1" /> Approve
                      </Button>
                      <Button size="sm" onClick={() => handleAdvanceAction(a, 'Denied')} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 px-3">
                        <X className="w-3 h-3 mr-1" /> Reject
                      </Button>
                    </div>
                  ) : (
                    <span className={cn(
                      "text-xs font-semibold px-2.5 py-0.5 rounded-full border flex-shrink-0",
                      a.status === 'Approved' ? 'text-green-700 dark:text-green-400 bg-green-500/15 dark:bg-green-500/10 border-green-500/40 dark:border-green-500/30' : 'text-red-700 dark:text-red-400 bg-red-500/15 dark:bg-red-500/10 border-red-500/40 dark:border-red-500/30'
                    )}>
                      {a.status}
                    </span>
                  )}
                </div>
              ))}
              {advances.length === 0 && <p className="text-center text-muted-foreground py-8 text-sm">No fuel advance requests.</p>}
            </div>
          </div>
        </div>}

        {/* ── SETTLEMENTS TAB ── */}
        {activeTab === 'settlements' && <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Total Settlements', value: filteredSettlements.length },
              { label: 'Pending', value: filteredSettlements.filter(s => s.status === 'Pending').length },
              { label: 'Approved', value: filteredSettlements.filter(s => s.status === 'Approved').length },
              { label: 'In Batch', value: filteredSettlements.filter(s => s.status === 'In Batch').length },
            ].map(s => (
              <div key={s.label} className="glass-card p-4 text-center">
                <p className="text-lg font-space font-bold text-foreground">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="glass-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left px-4 py-3 text-xs text-muted-foreground font-medium">Load #</th>
                    <th className="text-left px-4 py-3 text-xs text-muted-foreground font-medium">Driver</th>
                    <th className="text-right px-4 py-3 text-xs text-muted-foreground font-medium">Gross Rate</th>
                    {/* No fixed % in the header -- this table lists settlements that can
                        span rate changes over time, each already carrying its own correct
                        carrier_take_percent/dispatch_fee_percent-derived dollar amount below;
                        a single hardcoded "(15%)"/"(3%)" would misdescribe every row created
                        before or after a rate change. */}
                    <th className="text-right px-4 py-3 text-xs text-muted-foreground font-medium">Carrier Take</th>
                    <th className="text-right px-4 py-3 text-xs text-muted-foreground font-medium">Dispatch Fee</th>
                    <th className="text-right px-4 py-3 text-xs text-muted-foreground font-medium">Net to Driver</th>
                    <th className="text-right px-4 py-3 text-xs text-muted-foreground font-medium">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredSettlements.map(s => (
                    <tr key={s.id} className="hover:bg-white/3 transition-colors">
                      <td className="px-4 py-3 font-medium text-foreground">#{s.load_number}</td>
                      <td className="px-4 py-3 text-muted-foreground">{s.driver_name}</td>
                      <td className="px-4 py-3 text-right font-semibold text-foreground">${s.gross_revenue?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-red-400">−${s.carrier_take_amount?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-red-400">
                        {s.dispatch_fee_amount > 0 ? `−$${s.dispatch_fee_amount?.toFixed(2)}` : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-green-400">
                       ${(s.final_net_pay ?? s.net_driver_pay)?.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={cn(
                          "text-xs font-semibold px-2 py-0.5 rounded-full border",
                          s.status === 'Paid' ? 'text-green-700 dark:text-green-400 bg-green-500/15 dark:bg-green-500/10 border-green-500/40 dark:border-green-500/30' :
                          s.status === 'In Batch' ? 'text-blue-700 dark:text-blue-400 bg-blue-500/15 dark:bg-blue-500/10 border-blue-500/40 dark:border-blue-500/30' :
                          s.status === 'Approved' ? 'text-purple-700 dark:text-purple-400 bg-purple-500/15 dark:bg-purple-500/10 border-purple-500/40 dark:border-purple-500/30' :
                          'text-amber-700 dark:text-amber-400 bg-amber-500/15 dark:bg-amber-500/10 border-amber-500/40 dark:border-amber-500/30'
                        )}>{s.status}</span>
                      </td>
                      <td className="px-4 py-3 text-right flex items-center gap-2 justify-end">
                        {s.status === 'Pending' && (
                          isReleasedToAccounting(s) ? (
                            <Button size="sm" onClick={() => handleOpenReview(s)} className="bg-primary hover:bg-primary/90 text-primary-foreground h-7 px-2 text-xs">
                              <FileCheck className="w-3 h-3 mr-1" /> Review & Approve
                            </Button>
                          ) : (
                            <span className="text-xs text-amber-400 italic">Waiting on dispatch to release</span>
                          )
                        )}
                        {s.status === 'Approved' && (
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => client.entities.Settlement.update(s.id, { status: 'In Batch' }).then(loadAll)} className="bg-blue-600 hover:bg-blue-700 text-white h-7 px-2 text-xs">
                              Add to Batch
                            </Button>
                            <Button size="sm" onClick={() => { if (window.confirm(`Revert #${s.load_number} back to Pending to re-edit?`)) { client.entities.Settlement.update(s.id, { status: 'Pending', is_finalized: false, final_net_pay: null, pdf_url: null }).then(loadAll); } }} variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 h-7 px-2 text-xs">
                              Undo
                            </Button>
                          </div>
                        )}
                        {s.status === 'In Batch' && (
                          <span className="text-xs text-muted-foreground px-2 py-1">Awaiting payroll</span>
                        )}
                        {s.status === 'Paid' && (
                          <div className="flex items-center gap-2">
                            {s.pdf_url ? (
                              <a
                                href={s.pdf_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/10 border border-green-500/30 text-green-400 rounded-lg text-xs font-semibold hover:bg-green-500/20 transition-colors"
                              >
                                <Download className="w-3 h-3" /> View
                              </a>
                            ) : (
                              <RegeneratePdfButton settlement={s} onDone={loadAll} />
                            )}
                            <Button size="sm" onClick={() => handleRevertPaid(s)} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-7 px-2 text-xs">
                              Revert
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredSettlements.length === 0 && <p className="text-center text-muted-foreground py-8 text-sm">No settlements for this period.</p>}

            </div>
          </div>
        </div>}

        {/* ── PAYROLL TAB (Run Payroll + History, inner toggle) ── */}
        {activeTab === 'payroll' && <div className="space-y-4">
          <div className="flex gap-1 p-1 bg-white/5 rounded-xl w-fit">
            <button
              onClick={() => setPayrollView('run')}
              className={cn("px-4 py-2 rounded-lg text-sm font-medium transition-all", payrollView === 'run' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}
            >
              Run Payroll
            </button>
            {carrierPlan !== 'owner_op' && (
              <button
                onClick={() => setPayrollView('history')}
                className={cn("px-4 py-2 rounded-lg text-sm font-medium transition-all", payrollView === 'history' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}
              >
                History
              </button>
            )}
          </div>
          {payrollView === 'run' ? (
            <PayrollTab settlements={settlements} onRefresh={loadAll} />
          ) : (
            <PayrollHistoryTab />
          )}
        </div>}

        {/* ── PAY & DEDUCTIONS TAB (Insurance/ELD deductions, Carrier Take %) ── */}
        {activeTab === 'pay-settings' && <PayDeductionSettingsTab />}
        {activeTab === 'tax-forms' && <TaxFormsTab />}

        {/* ── DRIVERS TAB ── */}
        {activeTab === 'drivers' && <div>
          <DriverManagement openDriverId={dqfTargetDriverId} />
        </div>}

        {/* ── APPROVALS TAB ── */}
        {activeTab === 'approvals' && <div>
          <PendingApprovalsTab />
        </div>}

        {/* ── INVOICES / REPORTS PLACEHOLDERS ── */}
        {activeTab === 'broker-invoices' && (
          <BrokerInvoicesTab
            onInvoiceCreated={loadAll}
            onInvoiceDeleted={loadAll}
          />
        )}
        {activeTab === 'factoring' && (
          <div className="glass-card p-12 text-center">
            <p className="text-muted-foreground text-sm">Factoring integration — coming soon.</p>
          </div>
        )}
        {activeTab === 'report-tax' && <TaxReport />}
        {activeTab === 'report-pl' && <PLReport />}
        {activeTab === 'report-driver-settlements' && <DriverSettlementsReport />}
        {activeTab === 'report-driver-pay' && <DriverPayReport />}
        {activeTab === 'report-user-settlements' && <UserSettlementsReport />}
        {activeTab === 'report-carrier-settlements' && <CarrierSettlementsReport />}
        {activeTab === 'report-carrier-pay' && <CarrierPayReport />}
        {activeTab === 'report-dispatcher-settlements' && <DispatcherSettlementsReport />}
        {activeTab === 'report-dispatcher-management' && <DispatcherManagementReport />}
        {activeTab === 'report-expenses' && <ExpensesReport />}
        {activeTab === 'report-irp-miles' && <IRPStateMilesReport />}
        {activeTab === 'report-fuel-expenses' && <FuelExpensesReport />}
        {activeTab === 'report-reefer-fuel' && <ReeferFuelExpensesReport />}
        {activeTab === 'report-fuel-vendor' && <FuelVendorReport />}
        {activeTab === 'report-ifta' && <IFTAReport />}
        {activeTab === 'report-ifta-audit' && <IFTAAuditReport />}
        {activeTab === 'report-customer' && <CustomerReport />}
        {activeTab === 'report-unit-revenue' && <UnitRevenueReport />}
        {activeTab === 'report-unit-operating-income' && <UnitOperatingIncomeReport />}
        {activeTab === 'report-mpg' && <MPGReport />}
        {activeTab === 'report-unit-miles' && <UnitMilesReport />}
        {activeTab === 'report-miles' && <MilesReport />}
        {activeTab === 'report-maintenance' && <MaintenanceReport />}
        {activeTab === 'report-otr-days' && <OTRDaysReport />}
        {activeTab === 'report-lane' && <LaneReport />}

        {/* ── SETTINGS TABS ── */}
        {activeTab === 'company' && <div>
          <CompanySettingsTab />
        </div>}
        {activeTab === 'user-management' && <UserManagementTab />}
        {activeTab === 'billing' && <BillingTab />}
        {activeTab === 'address-book' && <div>
          <AddressBookTab />
        </div>}

        {/* ── ASSETS TAB ── */}
        {activeTab === 'assets' && (
          <AssetsTab
            alerts={maintenanceAlerts}
            editAssetId={assetTargetId}
            onViewHistory={(asset) => setActiveTab('service-records')}
          />
        )}

        {/* ── SERVICE RECORDS TAB ── */}
        {activeTab === 'service-records' && (
          <ServiceRecordsTab alerts={maintenanceAlerts} />
        )}

        {/* ── MAP TAB ── */}
        {activeTab === 'map' && (
          <div className="glass-card overflow-hidden" style={{ height: '600px' }}>
            <FleetMapPanel loads={loads} assets={maintenanceAssets} driverRoster={driverRoster} />
          </div>
        )}
      </div>{/* end tab content wrapper */}

      {editLoad && (
        <EditLoadModal
          load={editLoad}
          onClose={() => setEditLoad(null)}
          onSaved={loadAll}
        />
      )}

      {reviewSettlement && (
        <SettlementReviewModal
          settlement={reviewSettlement}
          onClose={() => setReviewSettlement(null)}
          onFinalized={loadAll}
        />
      )}

      {approveAdvance && (
        <FuelAdvanceApprovalModal
          advance={approveAdvance}
          onClose={() => setApproveAdvance(null)}
          onApproved={loadAll}
        />
      )}

      {detailLoad && (
        <LoadDetailModal
          load={detailLoad}
          onClose={() => setDetailLoad(null)}
          onRefresh={loadAll}
        />
      )}
    </div>
  );
}